import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from "react-redux";
import axios from 'axios';

const CreateCredit = () => {
  const [state, setState] = useState({
    loading: false,
    credit: {
      amount: '',
      receiver: '',
    },
    creditData: {
      credits: [],
      total: 0,
      page: 1,
      pages: 1
    },
    currentPage: 1,
    error: null,
    success: ''
  });

  const { currentUser } = useSelector((state) => state.user);
  const itemsPerPage = 5;

  const onChange = useCallback((e) => {
    const { name, value } = e.target;
    setState(prev => ({
      ...prev,
      credit: {
        ...prev.credit,
        [name]: value
      }
    }));
  }, []);

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    setState(prev => ({ ...prev, loading: true, error: null, success: '' }));
    
    try {
      await axios.post(`/api/credit/create/${currentUser._id}`, {
        amount: state.credit.amount,
        receiver: state.credit.receiver,
      });
      
      setState(prev => ({
        ...prev,
        loading: false,
        credit: { amount: '', receiver: '' },
        error: null,
        success: 'Credit created successfully!',
        currentPage: 1 // Reset to first page
      }));
      
      fetchgetCredit();
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err.response?.data?.message || 'Error creating credit',
        success: ''
      }));
    }
  }, [currentUser._id, state.credit.amount, state.credit.receiver]);

  const fetchgetCredit = useCallback(async () => {
    if (!currentUser?.phone) return;

    try {
      setState(prev => ({ ...prev, loading: true }));
      const res = await axios.get(`/api/credit/getCredit/${currentUser.phone}`, {
        params: {
          page: state.currentPage,
          limit: itemsPerPage
        }
      });
      
      setState(prev => ({
        ...prev,
        creditData: res.data,
        loading: false,
        error: null
      }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err.response?.data?.message || 'Error fetching credit'
      }));
    }
  }, [currentUser?.phone, state.currentPage]);

  useEffect(() => {
    fetchgetCredit();
  }, [fetchgetCredit]);

  const handlePageChange = useCallback((pageNumber) => {
    setState(prev => ({ ...prev, currentPage: pageNumber }));
  }, []);

  return (
    <div className="tw-form-container tw-max-w-4xl tw-mx-auto">
      <div className='tw-mb-5 tw-bg-white tw-p-6 tw-shadow-md tw-rounded-lg'>
        <h1 className='tw-text-2xl tw-text-blue-800'>Create <span className="tw-text-primary">Credit</span></h1>
        
        {state.error && (
          <div className="tw-mb-4 tw-p-3 tw-bg-red-100 tw-text-red-700 tw-rounded-md">
            {state.error}
          </div>
        )}

        {state.success && (
          <div className="tw-mb-4 tw-p-3 tw-bg-green-100 tw-text-green-700 tw-rounded-md">
            {state.success}
          </div>
        )}

        <form onSubmit={onSubmit} className='tw-flex tw-flex-col tw-gap-3'>
          <div className="tw-form-group">
            <label htmlFor="amount" className='tw-text-blue-800'>Amount</label>
            <input
              type="number"
              name="amount"
              value={state.credit.amount}
              onChange={onChange}
              required
              min="1"
              className='tw-ml-3 tw-p-1 tw-border tw-rounded'
            />
          </div>
          <div className="tw-form-group">
            <label htmlFor="toPhoneNumber" className='tw-text-blue-800'>Recipient Phone Number</label>
            <input
              type="tel"
              name="receiver"
              value={state.credit.receiver}
              onChange={onChange}
              required
              className='tw-ml-3 tw-p-1 tw-border tw-rounded'
            />
          </div>
          <button 
            type="submit" 
            className="tw-border-none btn btn-primary btn-block tw-bg-blue-800 tw-text-white tw-p-2 tw-w-[30%] tw-px-3 tw-rounded hover:tw-bg-blue-900" 
            disabled={state.loading}
          >
            {state.loading ? "Loading..." : "Create Credit"}
          </button>
        </form>
      </div>
      
      <div className='tw-mt-8 tw-bg-white tw-rounded-lg tw-shadow-lg tw-overflow-hidden'>
        <h2 className='tw-text-xl tw-font-semibold tw-p-4 tw-border-b tw-border-gray-200'>
          Credit History
        </h2>
        <div className="tw-overflow-x-auto">
          <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
            <thead className="tw-bg-blue-800">
              <tr>
                <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">Sender</th>
                <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">Receiver</th>
                <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">Amount</th>
                <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
              {state.creditData.credits.length > 0 ? (
                state.creditData.credits.map((credit) => (
                  <tr key={credit._id} className="hover:tw-bg-gray-50">
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">{credit.sender}</td>
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">{credit.receiver}</td>
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">${credit.amount}</td>
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                      {new Date(credit.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="tw-px-6 tw-py-4 tw-text-center tw-text-gray-500">
                    {state.loading ? 'Loading data...' : 'No records found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className="tw-flex tw-justify-between tw-items-center tw-p-4 tw-border-t tw-border-gray-200">
          <button
            onClick={() => handlePageChange(state.currentPage - 1)}
            disabled={state.currentPage === 1}
            className="tw-px-4 tw-py-2 tw-border tw-text-sm tw-font-medium tw-bg-white tw-text-blue-800 hover:tw-bg-gray-50 disabled:tw-opacity-50"
          >
            Previous
          </button>
          <span className="tw-text-sm tw-text-gray-700">
            Page {state.currentPage} of {state.creditData.pages}
          </span>
          <button
            onClick={() => handlePageChange(state.currentPage + 1)}
            disabled={state.currentPage >= state.creditData.pages}
            className="tw-px-4 tw-py-2 tw-border tw-text-sm tw-font-medium tw-bg-white tw-text-blue-800 hover:tw-bg-gray-50 disabled:tw-opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(CreateCredit);